package com.exura.pages.planCreator.components.seriesViews

import androidx.compose.runtime.*
import com.exura.features.plandefinition.domain.builder.series.SimpleQuestionSeriesBuilderNew
import com.exura.features.plandefinition.domain.model.plan.PlanType
import com.exura.features.plandefinition.domain.model.series.SeriesType
import com.exura.features.plandefinition.domain.model.series.SimpleQuestionSeries
import com.exura.pages.planCreator.components.ViewMode
import com.exura.pages.planCreator.components.seriesViews.inputs.*
import com.exura.pages.planCreator.components.seriesViews.sections.*
import com.exura.pages.planCreator.model.SeriesTypeRegistry
import com.exura.ui.Dimens
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import org.jetbrains.compose.web.css.px


@Composable
internal fun SimpleQuestionSeriesView(
    builder: SimpleQuestionSeriesBuilderNew,
    isNewSeries: Boolean = true,
    viewMode: ViewMode,
    planType: PlanType,
    onAction: () -> Unit,
) {
    val typeInfo = SeriesTypeRegistry.getTypeInfo(SeriesType.SIMPLE_QUESTION)!!
    val inputMode = if (viewMode == ViewMode.EDIT) PlanCreatorInputMode.EDIT else PlanCreatorInputMode.VIEW

    // Default view values - backing state variables
    var name by remember(builder) { mutableStateOf("") }
    var description by remember(builder) { mutableStateOf("") }
    var relativeDay by remember(builder) { mutableStateOf(0) }
    var duration by remember(builder) { mutableStateOf(1) }
    var question by remember(builder) { mutableStateOf("") }
    var options by remember(builder) { mutableStateOf(listOf<SimpleQuestionSeries.QuestionOption>()) }

    // Mode display values
    var relativeDayMode by remember(builder) { mutableStateOf(RelativeDayMode.ON_VISIT_DAY) }
    var relativeDayDisplay by remember(builder) { mutableStateOf(1) }

    // Change tracking
    var hasChanges by remember(builder) { mutableStateOf(false) }
    var isValid by remember(builder) { mutableStateOf(false) }

    // Computed derived state
    val seriesHasName by remember(name) { mutableStateOf(name.isNotBlank()) }
    val hasQuestion by remember(question) { mutableStateOf(question.isNotBlank()) }

    fun updateChanges() {
        hasChanges = builder.isChanged
        val validationMessages = builder.validate()
        isValid = validationMessages.isEmpty()
    }

    // Setting on launch/change - initialize from builder state
    LaunchedEffect(builder) {
        name = builder.name() ?: name
        description = builder.description() ?: description
        relativeDay = builder.relativeDay() ?: relativeDay
        duration = builder.duration() ?: duration
        question = builder.question() ?: question

        // Handle options with default yes/no for new series
        options = if (builder.options().isEmpty() && isNewSeries) {
            listOf(
                SimpleQuestionSeries.QuestionOption(
                    id = "yes",
                    text = "Tak",
                    responseMessage = null,
                    isResponsePositive = true
                ),
                SimpleQuestionSeries.QuestionOption(
                    id = "no",
                    text = "Nie",
                    responseMessage = null,
                    isResponsePositive = false
                )
            )
        } else {
            builder.options()
        }

        // Set mode display values based on stored values
        relativeDayMode = if (relativeDay == 0) RelativeDayMode.ON_VISIT_DAY else RelativeDayMode.DAYS_OFFSET
        relativeDayDisplay = when (planType) {
            PlanType.PREPLAN -> kotlin.math.abs(relativeDay).coerceAtLeast(1)
            PlanType.POSTPLAN -> relativeDay.coerceAtLeast(1)
        }

        // Apply initial values to builder
        builder.setName(name)
        builder.setDescription(description)
        builder.setRelativeDay(relativeDay)
        builder.setDuration(duration)
        builder.setQuestion(question)
        builder.setOptions(options)

//        builder.markSaved()
    }

    SeriesView(
        hasChanges = hasChanges,
        isValid = isValid,
        typeInfo = typeInfo,
        isNewSeries = isNewSeries,
        viewMode = inputMode,
        onAction = {
            onAction()
            builder.markSaved()
            updateChanges()
        },
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(32.px)
                .overflow(Overflow.Auto),
            verticalArrangement = Arrangement.spacedBy(0.px)
        ) {

            NameSeriesSection(
                value = name,
                onValueChange = { new ->
                    name = new
                    builder.setName(new)
                    updateChanges()
                },
                mode = inputMode,
            )

            DescriptionSeriesSection(
                value = description,
                onValueChange = { new ->
                    description = new
                    builder.setDescription(new)
                    updateChanges()
                },
                mode = inputMode,
                placeholder = "Opisz kontekst pytania...",
                enabled = seriesHasName,
            )

            ElementMainNameSection(
                value = question,
                onValueChange = { new ->
                    question = new
                    builder.setQuestion(new)
                    updateChanges()
                },
                mode = inputMode,
                title = "Pytanie",
                placeholder = "O co chcesz zapytać pacjenta?",
                enabled = seriesHasName,
            )

            QuestionDurationSection(
                duration = duration,
                onDurationChange = { new ->
                    duration = new
                    builder.setDuration(new)
                    updateChanges()
                },
                mode = inputMode,
                enabled = seriesHasName && hasQuestion,
            )

            RelativeDaySection(
                selectedValue = relativeDayMode,
                relativeDay = relativeDayDisplay,
                onRelativeDayChange = { new ->
                    relativeDayMode = RelativeDayMode.DAYS_OFFSET
                    relativeDayDisplay = new
                    relativeDay = when (planType) {
                        PlanType.PREPLAN -> -new
                        PlanType.POSTPLAN -> new
                    }
                    builder.setRelativeDay(relativeDay)
                    updateChanges()
                },
                onModeChange = { newMode ->
                    relativeDayMode = newMode
                    when(newMode) {
                        RelativeDayMode.NONE -> {}
                        RelativeDayMode.ON_VISIT_DAY -> {
                            builder.setRelativeDay(0)
                            relativeDay = 0
                        }
                        RelativeDayMode.DAYS_OFFSET -> {
                            relativeDay = when (planType) {
                                PlanType.PREPLAN -> -relativeDayDisplay
                                PlanType.POSTPLAN -> relativeDayDisplay
                            }
                            builder.setRelativeDay(relativeDay)
                        }
                    }
                    updateChanges()
                },
                mode = inputMode,
                enabled = seriesHasName && hasQuestion,
                planType = planType,
            )

            QuestionOptionsSection(
                options = options,
                onOptionsChange = { newOptions ->
                    options = newOptions
                    builder.setOptions(newOptions)
                    updateChanges()
                },
                mode = inputMode,
                enabled = hasQuestion,
            )

        }
    }
}